import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH, OKTA_CONFIG, OktaAuthStateService, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { countdownInSeconds } from 'src/util/rxjs/observables';

function log(msg: string, ...args: any[]) {
  if ((window as any).tcsDebug === true) {
    // eslint-disable-next-line no-console
    console.log(msg, ...(args ?? []));
  }
}

export function perfTest<T>(name: string, promise: Promise<T>): Promise<T> {
  log(`Executing (${name})...`);
  const start = performance.now();
  return promise.then((res) => {
    const diff = performance.now() - start;
    log(`Executed (${name}) in (${diff} ms)`);
    return res;
  });
}

@Component({
  selector: 'tcs-tcs-okta-callback',
  templateUrl: './tcs-okta-callback.component.html',
  styleUrls: ['./tcs-okta-callback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcsOktaCallbackComponent implements OnInit, OnDestroy {
  public error: string;
  public redirectUri = '/';
  public loaderContext$ = new ReplaySubject<{ message: string }>(1);

  private _showMessageInSeconds = 6;
  private _autoRedirectInSeconds = 20;
  private _errorRedirectInSeconds = 1;
  private _clearInterval$ = new Subject();

  constructor(
    @Inject(OKTA_CONFIG) private oktaConfig: OktaConfig,
    @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
    private _oktaState: OktaAuthStateService,
    private _router: Router,
    @Optional() private injector?: Injector,
  ) {
    try {
      // keep a copy of orginalUri, since after handleLoginRedirect() is called this function will return null.
      const uri = this._oktaAuth.getOriginalUri() ?? '/';
      if (!uri.includes('implicit/callback')) {
        this.redirectUri = uri;
      }
    } catch {}
  }

  public async ngOnInit() {
    try {
      this._createCountdownInterval({
        countdownFrom: this._autoRedirectInSeconds,
        displayMessageIn: this._showMessageInSeconds,
      });

      // Parse code or tokens from the URL, store tokens in the TokenManager, and redirect back to the originalUri
      await perfTest(
        'TcsOktaCallbackComponent.handleLoginRedirect',
        this._oktaAuth.handleLoginRedirect(),
      );
    } catch (e: any) {
      // Callback from social IDP. Show custom login page to continue.
      if (this._oktaAuth.idx.isInteractionRequiredError(e) && this.injector != null) {
        const { onAuthResume, onAuthRequired } = this.oktaConfig;
        const callbackFn = onAuthResume ?? onAuthRequired;
        if (callbackFn != null) {
          callbackFn(this._oktaAuth, this.injector);
          return;
        }
      }

      this.error = e.toString();

      this._createCountdownInterval({
        countdownFrom: this._errorRedirectInSeconds,
        displayMessageIn: this._showMessageInSeconds,
      });

      // eslint-disable-next-line no-console
      console.error('TcsOktaCallbackComponent:', e);
    }
  }

  public ngOnDestroy() {
    this._clearInterval$.next(null);
    this._clearInterval$.complete();
  }

  private _createCountdownInterval({
    countdownFrom,
    displayMessageIn,
  }: {
    countdownFrom: number;
    displayMessageIn: number;
  }) {
    this._clearInterval$.next(null);
    countdownInSeconds(countdownFrom)
      .pipe(takeUntil(this._clearInterval$))
      .subscribe((cnt) => {
        if (cnt <= 0) {
          window.location.href = this.redirectUri;
        } else if (countdownFrom - cnt >= displayMessageIn) {
          this.loaderContext$.next({
            message: `Redirecting within... (${cnt.toString().padStart(2, '0')} sec)`,
          });
        }
      });
  }
}
